import { useQuery } from '@apollo/client/react';
import React, { useEffect, useRef, useState } from 'react';
import styled, { css } from 'styled-components';
import {
  ExerciseDetailsFragment,
  MovementDetailDocument,
  MovementDetailExerciseFragment,
} from '../../graphql/types';
import {
  AQUAMARINE,
  BACKGROUND,
  BLACK2,
  BORDER_GRAY,
  FOCUS_OUTLINE,
  WHITE,
} from '../../theme';
import { EventCategories, ScreenNames } from '../../utils/trackingEvents';
import LoadingPanel from '../LoadingPanel';
import { ExercisePreview } from '../MemberProfile/TrainingPlans/ExercisePreview';
import Modal from '../Modal';
import {
  ButtonCSS,
  type ButtonCSSProps,
  ButtonSize,
  ButtonVariant,
} from '../reusable/Button';
import GenericError from '../reusable/GenericError';
import Text, { TextColor, TextKind } from '../reusable/Text';

const VideoWrap = styled.div`
  width: 100%;
  flex-shrink: 0;
  border-radius: 8px;
  overflow: hidden;
  background: ${BLACK2};
`;

const StyledVideo = styled.video`
  width: 100%;
  max-height: min(50vh, 480px);
  display: block;
`;

const ThumbnailOnly = styled.img`
  width: 100%;
  max-height: min(50vh, 480px);
  object-fit: contain;
  display: block;
`;

const HierarchyStack = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0;
  border: 1px solid ${BORDER_GRAY};
  border-radius: 8px;
  overflow: hidden;
`;

const HierarchyRow = styled.div`
  box-sizing: border-box;
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 12px;
  width: 100%;
  padding: 10px 14px;
  background: ${BACKGROUND};
  border-bottom: 1px solid ${BORDER_GRAY};

  &:last-child {
    border-bottom: none;
  }
`;

const ChipList = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
`;

const Chip = styled.span`
  display: inline-block;
  padding: 4px 10px;
  border-radius: 6px;
  border: 1px solid ${BORDER_GRAY};
  background: ${BACKGROUND};
`;

const Section = styled.section`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

/**
 * Do not allow Modal.Content’s column flex to shrink this block below its
 * content height — that caused overflow to paint over the sections below.
 */
const HierarchySection = styled(Section)`
  flex-shrink: 0;
`;

const CoachNotes = styled.div`
  white-space: pre-wrap;
  padding: 12px;
  border-radius: 8px;
  border: 1px solid ${BORDER_GRAY};
`;

const SimilarList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0;
  border: 1px solid ${BORDER_GRAY};
  border-radius: 6px;
  background: ${WHITE};
  overflow: hidden;
`;

const similarRowStyles = css`
  box-sizing: border-box;
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 12px;
  padding: 10px 12px;
  border-bottom: 1px solid ${BORDER_GRAY};
  background: ${WHITE};

  &:last-child {
    border-bottom: none;
  }
`;

const SimilarRow = styled.div`
  ${similarRowStyles}
`;

const ViewLabel = styled.div<ButtonCSSProps>`
  ${ButtonCSS}
  flex-shrink: 0;
`;

const SimilarRowButton = styled.button.attrs({ type: 'button' })`
  ${similarRowStyles}
  width: 100%;
  font: inherit;
  color: inherit;
  margin: 0;
  border: none;
  border-bottom: 1px solid ${BORDER_GRAY};
  cursor: pointer;
  text-align: left;
  appearance: none;

  &:last-child {
    border-bottom: none;
  }

  &:focus-visible {
    outline: ${FOCUS_OUTLINE};
  }
`;

const CurrentSimilarRow = styled(SimilarRow)`
  background: ${BACKGROUND};
  border-left: 3px solid ${AQUAMARINE};
  padding-left: 9px;
`;

const SimilarGroupLabel = styled.div`
  padding: 8px 12px 4px;
  background: ${BACKGROUND};
  border-bottom: 1px solid ${BORDER_GRAY};
`;

const ProgressionBadge = styled.span`
  flex-shrink: 0;
  width: 28px;
  text-align: center;
`;

const SimilarRowMeta = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
  flex: 1;
`;

export interface MovementDetailModalProps {
  exerciseId: string | null;
  onClose: () => void;
  onOpenExercise?: (exerciseId: string) => void;
  onReplace?: (exercise: MovementDetailExerciseFragment) => void;
}

export const MovementDetailModal: React.FC<MovementDetailModalProps> = ({
  exerciseId,
  onClose,
  onOpenExercise,
  onReplace,
}) => {
  const isOpen = exerciseId != null;
  const [initialExerciseId, setInitialExerciseId] = useState<string | null>(
    null,
  );
  const { data, previousData, loading, error } = useQuery(
    MovementDetailDocument,
    {
      variables: { id: exerciseId ?? '' },
      skip: !exerciseId,
      fetchPolicy: 'cache-and-network',
    },
  );

  // Track whether we have ever rendered an exercise during the *current* open
  // session. This lets us keep using `previousData` for a flicker-free
  // transition when navigating between similar movements within an open
  // modal, while avoiding flashing a stale exercise the first time the
  // modal is (re)opened on a different `exerciseId`.
  const hasRenderedThisSessionRef = useRef(false);
  useEffect(() => {
    if (!isOpen) {
      hasRenderedThisSessionRef.current = false;
    }
  }, [isOpen]);
  useEffect(() => {
    if (!isOpen) {
      setInitialExerciseId(null);
      return;
    }
    setInitialExerciseId((currentInitialId) => currentInitialId ?? exerciseId);
  }, [exerciseId, isOpen]);
  if (data?.exercise) {
    hasRenderedThisSessionRef.current = true;
  }

  const exercise =
    data?.exercise ??
    (hasRenderedThisSessionRef.current ? previousData?.exercise : undefined);
  const title = exercise?.displayName ?? exercise?.name ?? 'Movement';
  const base = exercise?.exerciseBase;
  const type = base?.exerciseType;
  const similarExercises = exercise?.similarExercises ?? [];
  const disableReplaceButton =
    initialExerciseId === null || exercise?.id === initialExerciseId;

  const similarListItems = buildSimilarList(exercise, similarExercises);

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      testId="movement-detail-modal"
      width="min(920px, 94vw)"
      trackingData={{
        event_category: EventCategories.Movements,
        screen_name: ScreenNames.MovementLibrary,
      }}
    >
      <Modal.Header onClose={onClose} closeLabel="Close movement details">
        <div id="modal-title">
          <Text kind={TextKind.TitleM} component="h2">
            {title}
          </Text>
        </div>
      </Modal.Header>
      <Modal.Content
        style={{
          maxHeight: '75vh',
          minHeight: 0,
          paddingBottom: 24,
        }}
      >
        {loading && !exercise && <LoadingPanel noBorder height={200} />}
        {!loading && error && !exercise && (
          <GenericError testId="movement-detail-error" error={error} />
        )}
        {!loading && !error && !exercise && (
          <Text kind={TextKind.BodyM}>This movement could not be loaded.</Text>
        )}
        {exercise && (
          <>
            {exercise.video ? (
              <VideoWrap>
                <StyledVideo
                  key={exercise.id}
                  controls
                  playsInline
                  autoPlay
                  muted
                  loop
                  poster={exercise.thumbnail ?? undefined}
                  src={exercise.video}
                  data-testid="movement-detail-video"
                >
                  <track kind="captions" />
                </StyledVideo>
              </VideoWrap>
            ) : exercise.thumbnail ? (
              <VideoWrap>
                <ThumbnailOnly
                  src={exercise.thumbnail}
                  alt=""
                  data-testid="movement-detail-thumbnail-only"
                />
              </VideoWrap>
            ) : (
              <Text kind={TextKind.BodyM} color={TextColor.Secondary}>
                No video or image available for this movement.
              </Text>
            )}

            {base ? (
              <HierarchySection>
                <HierarchyStack>
                  <HierarchyRow data-testid="movement-detail-base">
                    <Text kind={TextKind.BodyM} component="span">
                      <Text
                        kind={TextKind.BodyM}
                        color={TextColor.Secondary}
                        component="span"
                      >
                        Base movement:{' '}
                      </Text>
                      {base.name}
                    </Text>
                  </HierarchyRow>
                  {type && (
                    <HierarchyRow data-testid="movement-detail-movement-type">
                      <Text kind={TextKind.BodyM} component="span">
                        <Text
                          kind={TextKind.BodyM}
                          color={TextColor.Secondary}
                          component="span"
                        >
                          Movement type:{' '}
                        </Text>
                        {type.name}
                      </Text>
                    </HierarchyRow>
                  )}
                </HierarchyStack>
              </HierarchySection>
            ) : (
              <Text kind={TextKind.BodyM} color={TextColor.Secondary}>
                No base movement is linked for this record.
              </Text>
            )}

            {similarExercises.length > 0 && (
              <Section aria-labelledby="movement-similar-heading">
                <div id="movement-similar-heading">
                  <Text kind={TextKind.LabelL} component="h3">
                    Similar movements
                  </Text>
                </div>
                <SimilarList data-testid="movement-detail-similar-list">
                  {similarListItems.map((item) => {
                    if (item.kind === 'divider') {
                      return (
                        <SimilarGroupLabel key="cross-base-divider">
                          <Text
                            kind={TextKind.LabelS}
                            color={TextColor.Secondary}
                          >
                            Other base movements
                          </Text>
                        </SimilarGroupLabel>
                      );
                    }
                    return (
                      <SimilarMovementRow
                        key={item.id}
                        item={item}
                        onView={onOpenExercise}
                      />
                    );
                  })}
                </SimilarList>
              </Section>
            )}

            {exercise.defaultUnit && (
              <Section>
                <Text kind={TextKind.LabelL}>Default unit</Text>
                <Text kind={TextKind.BodyM}>{exercise.defaultUnit.name}</Text>
              </Section>
            )}

            {exercise.coachingKeys.length > 0 && (
              <Section>
                <Text kind={TextKind.LabelL}>Instructions</Text>
                <ul style={{ margin: 0, paddingLeft: 20 }}>
                  {exercise.coachingKeys.map((key) => (
                    <li key={key}>
                      <Text kind={TextKind.BodyM}>{key}</Text>
                    </li>
                  ))}
                </ul>
              </Section>
            )}

            {exercise.modifications ? (
              <Section>
                <Text kind={TextKind.LabelL}>Modifications</Text>
                <CoachNotes>
                  <Text kind={TextKind.BodyM}>{exercise.modifications}</Text>
                </CoachNotes>
              </Section>
            ) : null}

            {exercise.coachNotes ? (
              <Section>
                <Text kind={TextKind.LabelL}>Coach notes</Text>
                <CoachNotes>
                  <Text kind={TextKind.BodyM}>{exercise.coachNotes}</Text>
                </CoachNotes>
              </Section>
            ) : null}
            {exercise.equipment.length > 0 && (
              <>
                <Text kind={TextKind.LabelM} color={TextColor.Secondary}>
                  Equipment
                </Text>
                <ChipList role="list" aria-label="Equipment for this movement">
                  {exercise.equipment.map((item) => (
                    <Chip key={item} role="listitem">
                      <Text kind={TextKind.BodyS}>{item}</Text>
                    </Chip>
                  ))}
                </ChipList>
              </>
            )}
            {exercise.bodyParts.length > 0 && (
              <>
                <Text kind={TextKind.LabelM} color={TextColor.Secondary}>
                  Body parts
                </Text>
                <ChipList role="list" aria-label="Body parts for this movement">
                  {exercise.bodyParts.map((part) => (
                    <Chip key={part} role="listitem">
                      <Text kind={TextKind.BodyS}>{part}</Text>
                    </Chip>
                  ))}
                </ChipList>
              </>
            )}
          </>
        )}
      </Modal.Content>
      <Modal.Actions
        confirmButton={
          onReplace && exercise
            ? {
                label: 'Replace',
                testId: 'movement-detail-replace',
                disabled: disableReplaceButton,
                onClick: () => {
                  onReplace(exercise);
                  onClose();
                },
              }
            : {
                // Read-only view: no Replace action, and the X already
                // dismisses, so Done is the only thing left for the footer.
                label: 'Done',
                testId: 'movement-detail-close',
                onClick: onClose,
              }
        }
      />
    </Modal>
  );
};

export type SimilarItem = ExerciseDetailsFragment & {
  kind: 'exercise';
  sameBase: boolean;
  isCurrent: boolean;
};

export interface SimilarDivider {
  kind: 'divider';
}

export type SimilarListEntry = SimilarItem | SimilarDivider;

function sortByProgression(a: SimilarItem, b: SimilarItem): number {
  return (a.progression ?? Infinity) - (b.progression ?? Infinity);
}

export function buildSimilarList(
  exercise: ExerciseDetailsFragment | null | undefined,
  similarExercises: ExerciseDetailsFragment[],
): SimilarListEntry[] {
  if (!exercise) {
    return [];
  }

  const baseId = exercise.exerciseBase?.id;

  const toItem = (
    s: ExerciseDetailsFragment,
    isCurrent: boolean,
  ): SimilarItem => ({
    ...s,
    kind: 'exercise',
    sameBase: s.exerciseBase?.id === baseId,
    isCurrent,
  });

  const currentItem = toItem(exercise, true);

  const sameBaseSimilar = similarExercises
    .filter((s) => s.exerciseBase?.id === baseId)
    .map((s) => toItem(s, false))
    .sort(sortByProgression);

  const crossBaseSimilar = similarExercises
    .filter((s) => s.exerciseBase?.id !== baseId)
    .map((s) => toItem(s, false))
    .sort(sortByProgression);

  // Insert current exercise into same-base group at its progression position
  const insertIdx = sameBaseSimilar.findIndex(
    (s) => (s.progression ?? Infinity) > (exercise.progression ?? Infinity),
  );
  const sameBaseWithCurrent =
    insertIdx === -1
      ? [...sameBaseSimilar, currentItem]
      : [
          ...sameBaseSimilar.slice(0, insertIdx),
          currentItem,
          ...sameBaseSimilar.slice(insertIdx),
        ];

  if (crossBaseSimilar.length === 0) {
    return sameBaseWithCurrent;
  }

  const divider: SimilarDivider = { kind: 'divider' };
  return [...sameBaseWithCurrent, divider, ...crossBaseSimilar];
}

const SimilarMovementRow: React.FC<{
  item: SimilarItem;
  onView?: (id: string) => void;
}> = ({ item, onView }) => {
  const [playing, setPlaying] = useState(false);
  const title = item.displayName ?? item.name;
  const canOpen = Boolean(onView) && !item.isCurrent;

  const rowInner = (
    <>
      <ProgressionBadge>
        {item.progression != null && (
          <Text kind={TextKind.LabelS} color={TextColor.Secondary}>
            {item.progression}
          </Text>
        )}
      </ProgressionBadge>
      <ExercisePreview exercise={item} playing={playing} />
      <SimilarRowMeta>
        <Text kind={TextKind.BodyM}>{title}</Text>
        {!item.sameBase && item.exerciseBase?.name && (
          <Text kind={TextKind.BodyS} color={TextColor.Secondary}>
            {item.exerciseBase.name}
          </Text>
        )}
      </SimilarRowMeta>
    </>
  );

  if (item.isCurrent) {
    return (
      <CurrentSimilarRow
        onMouseEnter={() => setPlaying(true)}
        onMouseLeave={() => setPlaying(false)}
      >
        {rowInner}
        <Text kind={TextKind.LabelS} color={TextColor.Secondary}>
          Current
        </Text>
      </CurrentSimilarRow>
    );
  }

  if (canOpen && onView) {
    return (
      <SimilarRowButton
        onClick={() => onView(item.id)}
        onMouseEnter={() => setPlaying(true)}
        onMouseLeave={() => setPlaying(false)}
        onFocus={() => setPlaying(true)}
        onBlur={() => setPlaying(false)}
        aria-label={`View ${title}`}
      >
        {rowInner}
        <ViewLabel
          $size={ButtonSize.Medium}
          $variant={ButtonVariant.Outline}
          $fullWidth={false}
          $fullHeight={false}
          data-testid={`movement-detail-similar-view-${item.id}`}
        >
          <Text
            kind={TextKind.ButtonL}
            style={{ color: 'inherit', lineHeight: 1 }}
          >
            View
          </Text>
        </ViewLabel>
      </SimilarRowButton>
    );
  }

  return (
    <SimilarRow
      onMouseEnter={() => setPlaying(true)}
      onMouseLeave={() => setPlaying(false)}
    >
      {rowInner}
    </SimilarRow>
  );
};
